import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Search, Check, Globe, MapPin, ChevronRight, ArrowLeft, Loader2, Shield } from "lucide-react";
import { useGeoData, GeoCountry, GeoState, GeoCity } from "@/hooks/useGeoData";
import { useDebounce } from "@/hooks/useDebounce";
import type { City } from "@/types/location";

export interface LocationPickerValue {
  countryCode: string;
  countryName?: string;
  stateCode?: string;
  stateName?: string;
  stateId?: string;
  cityId?: string;
  cityName?: string;
}

interface CountryPickerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCountry: string;
  onSelect: (countryCode: string) => void;
  // Extended props for full location selection
  showStateCity?: boolean;
  selectedState?: string;
  selectedCity?: string;
  onSelectLocation?: (location: LocationPickerValue) => void;
  // Admin mode
  enableAdminMode?: boolean;
  isAdmin?: boolean;
}

type PickerScreen = "country" | "state" | "city";

const CountryPicker = ({ 
  isOpen, 
  onClose, 
  selectedCountry, 
  onSelect,
  showStateCity = false,
  selectedState,
  selectedCity,
  onSelectLocation,
  enableAdminMode = false,
  isAdmin = false,
}: CountryPickerProps) => {
  const [screen, setScreen] = useState<PickerScreen>("country");
  const [searchQuery, setSearchQuery] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [tempCountry, setTempCountry] = useState<GeoCountry | null>(null);
  const [tempState, setTempState] = useState<GeoState | null>(null);
  const [globalSearchMode, setGlobalSearchMode] = useState(false);
  
  // Geo data state
  const [countries, setCountries] = useState<GeoCountry[]>([]);
  const [states, setStates] = useState<GeoState[]>([]);
  const [cities, setCities] = useState<GeoCity[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const debouncedCitySearch = useDebounce(citySearch, 300);

  // Use useGeoData hook
  const { getCountries, getStates, getCities, requiresState, getCountryFlag } = useGeoData();
  
  // Use refs to store functions and avoid circular dependencies
  const getCountriesRef = useRef(getCountries);
  const getStatesRef = useRef(getStates);
  const getCitiesRef = useRef(getCities);
  const requiresStateRef = useRef(requiresState);
  
  // Update refs when functions change
  useEffect(() => {
    getCountriesRef.current = getCountries;
    getStatesRef.current = getStates;
    getCitiesRef.current = getCities;
    requiresStateRef.current = requiresState;
  }, [getCountries, getStates, getCities, requiresState]);
  
  // Global city search for admins - temporarily disabled to fix circular dependency
  const shouldUseGlobalSearch = false; // isAdmin && globalSearchMode; // Disabled to fix init error
  const globalCitiesData: City[] = [];
  const globalCitiesLoadingState = false;
  
  // Use appropriate city list based on mode
  const displayCities: (GeoCity | City)[] = useMemo(() => {
    if (shouldUseGlobalSearch && globalCitiesData.length > 0) {
      // Convert globalCities to GeoCity format
      return globalCitiesData.map(c => ({
        id: c.id,
        name: c.name,
        country_iso2: c.country_code || '',
        state_name: (c as any).state_name || null,
      })) as GeoCity[];
    }
    return cities;
  }, [shouldUseGlobalSearch, globalCitiesData, cities]);
  
  const citiesLoading = shouldUseGlobalSearch ? globalCitiesLoadingState : loadingCities;

  // Check if country has states
  const hasStates = useMemo(() => {
    if (!tempCountry || !requiresStateRef.current) return false;
    return requiresStateRef.current(tempCountry.iso2) || states.length > 0;
  }, [tempCountry, states]);
  
  // Get state/province label
  const stateLabel = useMemo(() => {
    if (!tempCountry) return "State";
    return tempCountry.iso2 === "CA" ? "Province" : "State";
  }, [tempCountry]);

  // Load countries when dialog opens
  useEffect(() => {
    if (!isOpen || screen !== "country" || countries.length > 0) return;
    if (!getCountriesRef.current) {
      console.warn("getCountries not ready yet");
      return;
    }
    
    let cancelled = false;
    const loadCountries = async () => {
      setLoadingCountries(true);
      try {
        const countryList = await getCountriesRef.current();
        if (!cancelled) {
          setCountries(countryList);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading countries:", error);
        }
      } finally {
        if (!cancelled) {
          setLoadingCountries(false);
        }
      }
    };
    loadCountries();
    
    return () => {
      cancelled = true;
    };
  }, [isOpen, screen, countries.length]);

  // Load states when country is selected
  useEffect(() => {
    // Early returns with detailed logging
    if (!showStateCity) {
      console.log("⏭️ Skipping state load: showStateCity is false");
      return;
    }
    if (!tempCountry) {
      console.log("⏭️ Skipping state load: no tempCountry");
      return;
    }
    if (screen !== "country") {
      console.log(`⏭️ Skipping state load: screen is "${screen}", need "country"`);
      return;
    }
    if (loadingStates) {
      console.log("⏭️ Skipping state load: already loading");
      return;
    }
    if (!getStatesRef.current || !requiresStateRef.current) {
      console.warn("⏭️ Skipping state load: refs not ready");
      return;
    }
    
    console.log(`🔄 [STATE LOAD] Starting for ${tempCountry.name} (${tempCountry.iso2})...`);
    
    let cancelled = false;
    const loadStatesForCountry = async () => {
      const needsStates = requiresStateRef.current(tempCountry.iso2);
      console.log(`📍 [STATE LOAD] Country ${tempCountry.iso2} needs states: ${needsStates}`);
      
      if (!needsStates) {
        // No states needed, go directly to cities
        console.log(`⏭️ [STATE LOAD] Skipping states, going to cities for ${tempCountry.name}`);
        if (!cancelled) {
          setScreen("city");
        }
        return;
      }
      
      console.log(`📥 [STATE LOAD] Fetching states for ${tempCountry.iso2}...`);
      setLoadingStates(true);
      try {
        const stateList = await getStatesRef.current(tempCountry.iso2);
        console.log(`✅ [STATE LOAD] Loaded ${stateList.length} states for ${tempCountry.iso2}`);
        
        if (!cancelled) {
          setStates(stateList);
          if (stateList.length > 0) {
            console.log(`✅ [STATE LOAD] Showing state selection screen with ${stateList.length} states`);
            setScreen("state");
          } else {
            console.log(`⚠️ [STATE LOAD] No states found, going to cities`);
            setScreen("city");
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("❌ [STATE LOAD] Error loading states:", error);
          setScreen("city");
        }
      } finally {
        if (!cancelled) {
          setLoadingStates(false);
        }
      }
    };
    
    loadStatesForCountry();
    
    return () => {
      cancelled = true;
    };
  }, [showStateCity, tempCountry, screen, loadingStates]);

  // Load cities when state is selected or when going to city screen
  useEffect(() => {
    // Early returns with detailed logging
    if (!showStateCity) {
      console.log("⏭️ Skipping city load: showStateCity is false");
      return;
    }
    if (!tempCountry) {
      console.log("⏭️ Skipping city load: no tempCountry");
      return;
    }
    if (screen !== "city") {
      console.log(`⏭️ Skipping city load: screen is "${screen}", need "city"`);
      return;
    }
    // Note: We allow loading even if already loading to handle state changes
    // The cancellation logic will prevent duplicate requests
    if (!getCitiesRef.current) {
      console.warn("⏭️ Skipping city load: ref not ready");
      return;
    }
    
    // For USA/Canada, require state to be selected before loading cities
    const needsState = requiresStateRef.current(tempCountry.iso2);
    const stateName = tempState?.name || undefined;
    
    if (needsState && !stateName) {
      console.log(`⏭️ [CITY LOAD] Skipping: ${tempCountry.iso2} requires state but none selected`);
      return;
    }
    
    console.log(`🔄 [CITY LOAD] Starting for ${tempCountry.name} (${tempCountry.iso2})${stateName ? `, ${stateName}` : ""}...`);
    
    let cancelled = false;
    const loadCitiesForLocation = async () => {
      console.log(`🚀 [CITY LOAD] Starting async load...`);
      setLoadingCities(true);
      try {
        const searchTerm = debouncedCitySearch || undefined;
        
        console.log(`📥 [CITY LOAD] Fetching: country=${tempCountry.iso2}, state=${stateName || "none"}, search=${searchTerm || "none"}`);
        
        const cityList = await getCitiesRef.current(
          tempCountry.iso2,
          stateName,
          searchTerm
        );
        
        console.log(`✅ [CITY LOAD] Received ${cityList.length} cities from API`);
        
        if (!cancelled) {
          console.log(`📝 [CITY LOAD] Setting ${cityList.length} cities in state...`);
          setCities(cityList);
          console.log(`✅ [CITY LOAD] Cities state updated`);
          
          if (cityList.length === 0) {
            console.warn(`⚠️ [CITY LOAD] No cities found for ${tempCountry.name}${stateName ? `, ${stateName}` : ""}`);
          } else {
            console.log(`✅ [CITY LOAD] Successfully set ${cityList.length} cities - UI should update now`);
          }
        } else {
          console.log(`⏭️ [CITY LOAD] Cancelled, not setting cities`);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("❌ [CITY LOAD] Error loading cities:", error);
          setCities([]);
        }
      } finally {
        if (!cancelled) {
          console.log(`🏁 [CITY LOAD] Setting loadingCities to false`);
          setLoadingCities(false);
        } else {
          console.log(`⏭️ [CITY LOAD] Cancelled, not updating loading state`);
        }
      }
    };
    
    loadCitiesForLocation();
    
    return () => {
      cancelled = true;
    };
  }, [showStateCity, tempCountry, tempState, screen, loadingCities, debouncedCitySearch]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setScreen("country");
      setSearchQuery("");
      setCitySearch("");
      setTempCountry(null);
      setTempState(null);
      setGlobalSearchMode(false);
      setStates([]);
      setCities([]);
    }
  }, [isOpen]);

  const filteredCountries = useMemo(() => 
    countries.filter((country) =>
      country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.iso2.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    [countries, searchQuery]
  );

  const filteredCities = useMemo(() => {
    if (!debouncedCitySearch) return displayCities;
    const searchLower = debouncedCitySearch.toLowerCase();
    return displayCities.filter(city => 
      city.name.toLowerCase().includes(searchLower)
    );
  }, [displayCities, debouncedCitySearch]);

  const handleSelectCountry = useCallback((country: GeoCountry) => {
    console.log(`🌍 Country selected: ${country.name} (${country.iso2}), showStateCity: ${showStateCity}`);
    
    if (!showStateCity) {
      // Simple mode - just select country
      onSelect(country.iso2);
      onClose();
      setSearchQuery("");
      return;
    }

    // Extended mode - check for states
    console.log(`📋 Extended mode: Setting tempCountry and checking for states...`);
    setTempCountry(country);
    setSearchQuery("");
    setStates([]);
    setCities([]);
    setTempState(null); // Reset state selection
    setScreen("country"); // Ensure we're on country screen so useEffect can run
    setLoadingStates(false); // Reset loading state
    setLoadingCities(false); // Reset loading state
  }, [showStateCity, onSelect, onClose]);

  const handleSelectState = useCallback((state: GeoState) => {
    console.log(`📍 [STATE SELECT] State selected: ${state.name} for ${tempCountry?.name} (${tempCountry?.iso2})`);
    setTempState(state);
    setCitySearch("");
    setCities([]); // Clear previous cities
    setLoadingCities(false); // Reset loading state
    setScreen("city"); // This will trigger the cities useEffect
    console.log(`📍 [STATE SELECT] Screen set to "city", cities should load now`);
  }, [tempCountry]);

  const handleSkipState = useCallback(() => {
    setTempState(null);
    setCitySearch("");
    setCities([]);
    setScreen("city");
  }, []);

  const handleSelectCity = useCallback((city: GeoCity | City) => {
    if (onSelectLocation && tempCountry) {
      onSelectLocation({
        countryCode: tempCountry.iso2,
        countryName: tempCountry.name,
        stateCode: tempState?.code || undefined,
        stateName: tempState?.name || undefined,
        stateId: tempState?.id || undefined,
        cityId: city.id,
        cityName: city.name,
      });
    }
    onClose();
  }, [tempCountry, tempState, onSelectLocation, onClose]);

  // Handle global city selection (includes country/state data)
  const handleGlobalCitySelect = useCallback((city: City & { country_name?: string; state_name?: string }) => {
    const country = countries.find((c) => c.iso2 === city.country_code);
    
    if (onSelectLocation) {
      onSelectLocation({
        countryCode: city.country_code,
        countryName: city.country_name || country?.name,
        stateCode: undefined,
        stateName: city.state_name,
        stateId: city.state_id || undefined,
        cityId: city.id,
        cityName: city.name,
      });
    }
    onClose();
  }, [countries, onSelectLocation, onClose]);

  const handleSelectCountryOnly = useCallback(() => {
    if (tempCountry) {
      if (onSelectLocation) {
        onSelectLocation({
          countryCode: tempCountry.iso2,
          countryName: tempCountry.name,
          stateCode: tempState?.code || undefined,
          stateName: tempState?.name || undefined,
          stateId: tempState?.id || undefined,
        });
      } else {
        onSelect(tempCountry.iso2);
      }
    }
    onClose();
  }, [tempCountry, tempState, onSelectLocation, onSelect, onClose]);

  const handleBack = useCallback(() => {
    if (screen === "city") {
      if (hasStates) {
        setScreen("state");
        setTempState(null);
      } else {
        setScreen("country");
        setTempCountry(null);
      }
      setCitySearch("");
    } else if (screen === "state") {
      setScreen("country");
      setTempCountry(null);
    }
  }, [screen, hasStates]);

  const handleSelectAny = useCallback(() => {
    onSelect("any");
    onClose();
    setSearchQuery("");
  }, [onSelect, onClose]);
  
  // Toggle global search mode
  const handleGlobalModeToggle = useCallback((enabled: boolean) => {
    setGlobalSearchMode(enabled);
    if (enabled) {
      setScreen("city");
      setTempCountry(null);
      setTempState(null);
      setCitySearch("");
    } else {
      setScreen("country");
    }
  }, []);

  const getTitle = () => {
    if (isAdmin && globalSearchMode) return "Global City Search";
    if (screen === "city") return tempState ? `Cities in ${tempState.name}` : `Cities in ${tempCountry?.name || ""}`;
    if (screen === "state") return `${stateLabel}s in ${tempCountry?.name || ""}`;
    return "Select Country";
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm mx-auto max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {screen !== "country" && !globalSearchMode && (
              <button 
                onClick={handleBack}
                className="p-1 hover:bg-muted rounded-md transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            {screen === "country" && !globalSearchMode && <Globe className="w-5 h-5" />}
            {isAdmin && globalSearchMode && <Shield className="w-5 h-5 text-amber-500" />}
            {screen === "state" && <MapPin className="w-5 h-5" />}
            {screen === "city" && !globalSearchMode && <MapPin className="w-5 h-5" />}
            {getTitle()}
          </DialogTitle>
        </DialogHeader>
        
        {/* Admin Global Search Toggle - Temporarily disabled to fix circular dependency */}
        {false && enableAdminMode && isAdmin && (
          <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 -mt-2">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
                Admin: Global Search
              </span>
            </div>
            <Switch
              checked={globalSearchMode}
              onCheckedChange={handleGlobalModeToggle}
            />
          </div>
        )}

        {/* Country Screen */}
        {screen === "country" && !globalSearchMode && (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search countries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <ScrollArea className="h-[300px] -mx-2">
              <div className="space-y-1 px-2">
                {/* Any Country Option */}
                <button
                  onClick={handleSelectAny}
                  className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                    selectedCountry === "any"
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">🌍</span>
                    <span className="font-medium">Any Country</span>
                  </div>
                  {selectedCountry === "any" && <Check className="w-4 h-4" />}
                </button>

                {loadingCountries ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  filteredCountries.map((country) => (
                    <button
                      key={country.iso2}
                      onClick={() => handleSelectCountry(country)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                        selectedCountry === country.iso2
                          ? "bg-primary/10 text-primary"
                          : "hover:bg-muted"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{getCountryFlag(country.iso2)}</span>
                        <span className="font-medium">{country.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedCountry === country.iso2 && <Check className="w-4 h-4" />}
                        {showStateCity && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                      </div>
                    </button>
                  ))
                )}

                {!loadingCountries && filteredCountries.length === 0 && searchQuery && (
                  <div className="text-center py-8 text-muted-foreground">
                    No countries found for "{searchQuery}"
                  </div>
                )}
              </div>
            </ScrollArea>
          </>
        )}
        
        {/* Global City Search (Admin Only) - Temporarily disabled */}
        {false && isAdmin && globalSearchMode && (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Type 2+ chars to search worldwide..."
                value={citySearch}
                onChange={(e) => setCitySearch(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>

            <ScrollArea className="h-[300px] -mx-2">
              <div className="space-y-1 px-2">
                {citiesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : debouncedCitySearch.length < 2 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Type at least 2 characters to search
                  </div>
                ) : cities.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No cities found for "{debouncedCitySearch}"
                  </div>
                ) : (
                  cities.map((city: any) => (
                    <button
                      key={city.id}
                      onClick={() => handleGlobalCitySelect(city)}
                      className="w-full flex items-center justify-between p-3 rounded-lg transition-colors hover:bg-muted"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <MapPin className="w-5 h-5 text-primary shrink-0" />
                        <div className="min-w-0 text-left">
                          <span className="font-medium block truncate">{city.name}</span>
                          {(city.state_name || city.country_name) && (
                            <span className="text-xs text-muted-foreground block truncate">
                              {[city.state_name, city.country_name].filter(Boolean).join(", ")}
                            </span>
                          )}
                        </div>
                      </div>
                      {selectedCity === city.id && <Check className="w-4 h-4 shrink-0" />}
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>

            {!citiesLoading && cities.length > 0 && (
              <p className="text-xs text-center text-muted-foreground">
                {cities.length} cities found worldwide
              </p>
            )}
          </>
        )}

        {/* State Screen */}
        {screen === "state" && !globalSearchMode && (
          <>
            <ScrollArea className="h-[300px] -mx-2">
              <div className="space-y-1 px-2">
                {/* Skip to all cities option */}
                <button
                  onClick={handleSkipState}
                  className="w-full flex items-center justify-between p-3 rounded-lg transition-colors hover:bg-muted border border-dashed border-border"
                >
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-muted-foreground" />
                    <span className="text-muted-foreground">All cities in {tempCountry?.name}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>

                {loadingStates ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  states.map((state) => (
                    <button
                      key={state.id}
                      onClick={() => handleSelectState(state)}
                      className="w-full flex items-center justify-between p-3 rounded-lg transition-colors hover:bg-muted"
                    >
                      <div className="flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-primary" />
                        <span className="font-medium">{state.name}</span>
                        {state.code && (
                          <span className="text-xs text-muted-foreground">({state.code})</span>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </>
        )}

        {/* City Screen */}
        {screen === "city" && !globalSearchMode && (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search cities..."
                value={citySearch}
                onChange={(e) => setCitySearch(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Skip city selection */}
            <button
              onClick={handleSelectCountryOnly}
              className="w-full flex items-center justify-between p-2 rounded-lg transition-colors hover:bg-muted border border-dashed border-border text-sm"
            >
              <span className="text-muted-foreground">
                {tempState ? `Any city in ${tempState.name}` : `Any city in ${tempCountry?.name}`}
              </span>
              <Check className="w-4 h-4 text-muted-foreground" />
            </button>

            <ScrollArea className="h-[250px] -mx-2">
              <div className="space-y-1 px-2">
                {citiesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredCities.length === 0 && !citiesLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {debouncedCitySearch ? `No cities found for "${debouncedCitySearch}"` : cities.length === 0 ? "No cities available" : "No cities match your search"}
                  </div>
                ) : filteredCities.length > 0 ? (
                  filteredCities.map((city) => (
                    <button
                      key={city.id}
                      onClick={() => handleSelectCity(city)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                        selectedCity === city.id
                          ? "bg-primary/10 text-primary"
                          : "hover:bg-muted"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-primary" />
                        <span className="font-medium">{city.name}</span>
                        {city.state_name && (
                          <span className="text-xs text-muted-foreground">({city.state_name})</span>
                        )}
                      </div>
                      {selectedCity === city.id && <Check className="w-4 h-4" />}
                    </button>
                  ))
                ) : null}
              </div>
            </ScrollArea>

            {!citiesLoading && filteredCities.length > 0 && (
              <p className="text-xs text-center text-muted-foreground">
                {filteredCities.length} cities found
              </p>
            )}
          </>
        )}

        {/* Loading indicator when checking for states */}
        {screen === "country" && tempCountry && loadingStates && !globalSearchMode && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center rounded-lg">
              <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">Loading {stateLabel.toLowerCase()}s...</span>
              </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CountryPicker;
